import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowRight, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2452ebe5-8b88-4541-a707-a9ae800f68db/artifacts/oj0fg93b_ChatGPT%20Image%2011%20mrt%202026%2C%2015_21_20.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(i18n.language === 'nl' 
        ? 'Ongeldige reset link. Vraag een nieuwe aan.' 
        : 'Invalid reset link. Please request a new one.');
    }
  }, [token, i18n.language]);

  const validatePassword = () => {
    if (password.length < 8) {
      return i18n.language === 'nl' 
        ? 'Wachtwoord moet minimaal 8 tekens bevatten' 
        : 'Password must be at least 8 characters';
    }
    if (password !== confirmPassword) {
      return i18n.language === 'nl' 
        ? 'Wachtwoorden komen niet overeen' 
        : 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(i18n.language === 'nl' 
        ? 'Wachtwoord succesvol gereset!' 
        : 'Password reset successfully!');
    } catch (err) {
      console.error('Password reset failed:', err);
      const errorMsg = err.response?.data?.detail || (i18n.language === 'nl' 
        ? 'Wachtwoord reset mislukt. De link is mogelijk verlopen.' 
        : 'Password reset failed. The link may have expired.');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-800 relative overflow-hidden" data-testid="reset-password-page">
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
      
      {/* Content */}
      <div className="container-custom py-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="Elementary Cargo" 
                className="h-20 mx-auto mb-4"
              />
            </Link>
            <h1 className="font-outfit text-2xl font-bold text-white mb-2">
              {i18n.language === 'nl' ? 'Nieuw Wachtwoord Instellen' : 'Set New Password'}
            </h1>
            <p className="text-steel-400">
              {i18n.language === 'nl' 
                ? 'Kies een sterk wachtwoord voor uw account' 
                : 'Choose a strong password for your account'}
            </p>
          </div>

          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              {error ? (
                <div className="text-center py-6" data-testid="reset-error">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-red-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-800 mb-2">
                    {i18n.language === 'nl' ? 'Link Ongeldig' : 'Invalid Link'}
                  </h3>
                  <p className="text-steel-600 mb-6">{error}</p>
                  <Link to="/forgot-password">
                    <Button className="bg-gold hover:bg-gold/90 text-navy-900">
                      {i18n.language === 'nl' ? 'Nieuwe Link Aanvragen' : 'Request New Link'}
                    </Button>
                  </Link>
                </div>
              ) : success ? (
                <div className="text-center py-6" data-testid="reset-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-800 mb-2">
                    {i18n.language === 'nl' ? 'Wachtwoord Gereset!' : 'Password Reset!'}
                  </h3>
                  <p className="text-steel-600 mb-6">
                    {i18n.language === 'nl' 
                      ? 'Uw wachtwoord is succesvol gewijzigd. U kunt nu inloggen met uw nieuwe wachtwoord.' 
                      : 'Your password has been changed successfully. You can now log in with your new password.'}
                  </p>
                  <Link to="/login">
                    <Button className="bg-gold hover:bg-gold/90 text-navy-900">
                      {i18n.language === 'nl' ? 'Ga naar Inloggen' : 'Go to Login'}
                      <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
                  <div>
                    <Label className="text-navy-800">
                      {i18n.language === 'nl' ? 'Nieuw Wachtwoord' : 'New Password'}
                    </Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" aria-hidden="true" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder={i18n.language === 'nl' ? 'Min. 8 tekens' : 'Min. 8 characters'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-12"
                        data-testid="reset-password-input"
                        aria-label="New password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-navy-800"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-navy-800">
                      {i18n.language === 'nl' ? 'Bevestig Wachtwoord' : 'Confirm Password'}
                    </Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" aria-hidden="true" />
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={i18n.language === 'nl' ? 'Bevestig uw wachtwoord' : 'Confirm your password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 pr-10 h-12"
                        data-testid="reset-confirm-input"
                        aria-label="Confirm password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-navy-800"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Password strength hints */}
                  <div className="text-sm text-steel-500">
                    <p className={password.length >= 8 ? 'text-green-600' : ''}>
                      {password.length >= 8 ? '✓' : '○'} {i18n.language === 'nl' ? 'Minimaal 8 tekens' : 'At least 8 characters'}
                    </p>
                    <p className={password && password === confirmPassword ? 'text-green-600' : ''}>
                      {password && password === confirmPassword ? '✓' : '○'} {i18n.language === 'nl' ? 'Wachtwoorden komen overeen' : 'Passwords match'}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gold hover:bg-gold/90 text-navy-900 font-semibold"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? (
                      i18n.language === 'nl' ? 'Bezig met opslaan...' : 'Saving...'
                    ) : (
                      <>
                        {i18n.language === 'nl' ? 'Wachtwoord Opslaan' : 'Save Password'}
                        <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
